import { useCallback, useRef } from "react";
import type { ReactNode } from "react";
import { useAuth } from "../../auth/authContext.tsx";
import { buildSessionBridgeUrl } from "../../api/auth.ts";

interface BridgedLinkProps {
  /** Same-origin path to open. The component opens this in a new tab with
   *  the current session bridged across so the receiver doesn't bounce
   *  through SignIn. Pass plain external URLs (e.g. google.com) through a
   *  regular `<a>` instead — bridging only helps when the destination is
   *  this app. */
  target: string;
  children: ReactNode;
  className?: string;
  title?: string;
}

/** Anchor that lazily mints a session-bridge URL on the first interaction
 *  and reverts to the plain `target` after the click is dispatched. The
 *  bridge entry self-destructs after 10 s and is one-shot — see
 *  `buildSessionBridgeUrl` / `consumeSessionBridge` in `api/auth.ts`.
 *
 *  Use this for any anchor or button that opens a same-origin route in a
 *  new tab via `target="_blank"`. The bridge guarantees the new tab inherits
 *  the current session without surfacing the token in the URL or DOM at
 *  rest. Right-click → "Open in new tab" works too; that path doesn't fire
 *  a disarm event but the localStorage entry self-destructs in 10 s. */
export function BridgedLink({ target, children, className, title }: BridgedLinkProps) {
  const { session } = useAuth();
  const ref = useRef<HTMLAnchorElement>(null);
  /* Idempotency: a single click typically fires onMouseDown *and* onFocus
   * (mousedown grants focus) — without this flag we'd mint two handoffs. */
  const armedRef = useRef(false);
  const arm = useCallback(() => {
    if (armedRef.current) return;
    const a = ref.current;
    if (!a || !session) return;
    a.href = buildSessionBridgeUrl(target, session);
    armedRef.current = true;
  }, [target, session]);
  /* Defer to next macrotask so the browser navigates with the bridged href
   * before we revert. Covers left-click (onClick), middle-click (onAuxClick),
   * keyboard Enter (onClick on focused anchor), and tab-away (onBlur). */
  const disarm = useCallback(() => {
    setTimeout(() => {
      if (ref.current) ref.current.href = target;
      armedRef.current = false;
    }, 0);
  }, [target]);
  return (
    <a
      ref={ref}
      href={target}
      target="_blank"
      rel="noopener"
      className={className}
      title={title}
      onMouseDown={arm}
      onFocus={arm}
      onClick={disarm}
      onAuxClick={disarm}
      onBlur={disarm}
    >{children}</a>
  );
}
